import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import { ClientAbsence, UpdateClientAbsenceOptions } from 'RestAPIs/absence';

import {
  Box,
  Button,
  Collapse,
  DialogActions,
  Grid,
  Stack
} from '@mui/material';

import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker,
  ControlledToggleButtonGroup
} from 'components/shared';

import { useCreateAbsence, useUpdateClientAbsence } from '../../mutations';
import { useGetClientAbsenceReasons } from '../../queries';
import addAbsenceSchema from './add-absence-schema';

const schema = addAbsenceSchema();

type Form = InferType<typeof schema>;

type Props = {
  appointmentId?: string;
  appointmentStart: string;
  clientId: string | undefined;
  clientName: string;
  handleClose: () => void;
  clientAbsence?: ClientAbsence;
};

export default function AddAbsenceForm({
  appointmentId,
  appointmentStart,
  clientId,
  clientName,
  handleClose,
  clientAbsence
}: Props) {
  const { t } = useTranslation('common');
  const { data: absenceReasons } = useGetClientAbsenceReasons();
  const { mutate: createClientAbsence } = useCreateAbsence();
  const { mutate: updateClientAbsence } = useUpdateClientAbsence();

  const isNewAppointmentAbsence = !clientAbsence && appointmentId;

  const defaultValues = clientAbsence
    ? {
        reasonId: clientAbsence.reasonId,
        description: clientAbsence.description,
        noEndDate:
          !clientAbsence.end ||
          clientAbsence.end.includes('9999') ||
          !dayjs(clientAbsence.end).isValid(),
        startDate: dayjs(clientAbsence.start),
        endDate: dayjs(clientAbsence.end),
        startTime: dayjs(clientAbsence.start),
        endTime: dayjs(clientAbsence.end)
      }
    : {
        description: '',
        startDate: dayjs(appointmentStart),
        endDate: dayjs(appointmentStart),
        startTime: dayjs(appointmentStart).startOf('day'),
        endTime: dayjs(appointmentStart).endOf('day'),
        noEndDate: !appointmentId,
        isAbsenceForAllAppointments: undefined,
        isNewAppointmentAbsence
      };

  const { control, handleSubmit, watch } = useForm<Form>({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const { noEndDate, absenceAppointmentType } = watch();

  function onSubmit(data: Form) {
    const {
      reasonId,
      description,
      noEndDate,
      startDate,
      startTime,
      endDate,
      endTime
    } = data;

    const newStartDate = startDate
      .hour(startTime.hour())
      .minute(startTime.minute());
    const newEndDate = endDate?.hour(endTime.hour()).minute(endTime.minute());

    if (clientId !== undefined) {
      const payload = {
        clientId,
        reasonId,
        description,
        start: newStartDate.toDate(),
        end: noEndDate ? null : newEndDate?.toDate(),
        absentAppointments:
          isNewAppointmentAbsence && absenceAppointmentType !== 'ALL'
            ? [{ id: appointmentId, originalId: appointmentId }]
            : null
      };

      if (clientAbsence) {
        const editPayload = {
          ...payload,
          clientAbsenceId: clientAbsence.clientAbsenceId,
          absentAppointments: clientAbsence.absentAppointments
        } as UpdateClientAbsenceOptions;

        updateClientAbsence(editPayload);
      } else {
        createClientAbsence(payload);
      }
      handleClose();
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ControlledSelect
              name="reasonId"
              control={control}
              label={t('reasonAbsence')}
              options={
                absenceReasons?.map(({ id, name }) => ({
                  label: name,
                  value: id
                })) ?? []
              }
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              name="description"
              control={control}
              label={t('description')}
              multiline
            />
          </Grid>
          <Grid item xs={4} mt={1}>
            <ControlledDatePicker
              name="startDate"
              control={control}
              label={t('startDate')}
            />
          </Grid>
          <Grid item xs={4} mt={1}>
            <ControlledTimePicker
              name="startTime"
              control={control}
              label={t('startTime')}
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledCheckbox
              name="noEndDate"
              control={control}
              label="Geen einddatum"
            />
          </Grid>
          <Grid item xs={12}>
            <Collapse in={!noEndDate} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <ControlledDatePicker
                    name="endDate"
                    control={control}
                    label={t('endDate')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ControlledTimePicker
                    name="endTime"
                    control={control}
                    label={t('endTime')}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
          {isNewAppointmentAbsence && (
            <Grid item xs={12}>
              <ControlledToggleButtonGroup
                control={control}
                name="absenceAppointmentType"
                color="secondary"
                aria-label="alignment"
                label={t('activityPlanning.absenceAppointmentType')}
                inputToggleButtons={[
                  {
                    label: t('activityPlanning.thisAppointment'),
                    value: 'SINGLE',
                    selected: absenceAppointmentType === 'SINGLE'
                  },
                  {
                    label: t('activityPlanning.allAppointments'),
                    value: 'ALL',
                    selected: absenceAppointmentType === 'ALL'
                  }
                ]}
              />
            </Grid>
          )}
        </Grid>
        {!isNewAppointmentAbsence ||
          (isNewAppointmentAbsence && absenceAppointmentType !== undefined && (
            <Box>
              {absenceAppointmentType === 'ALL'
                ? t(
                    'activityPlanning.addAbsenceDialog.allAppointmentsHelperText',
                    {
                      clientName
                    }
                  )
                : t(
                    'activityPlanning.addAbsenceDialog.singleAppointmentHelperText',
                    {
                      clientName
                    }
                  )}
            </Box>
          ))}
      </Stack>
      <DialogActions sx={{ mt: 3 }}>
        <Button type="button" variant="text" color="dark" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button type="submit" variant="contained" color="dark">
          {t('save')}
        </Button>
      </DialogActions>
    </form>
  );
}
